<template>
  <div class="grid grid-cols-[44px_3px_28px_minmax(0,1fr)_auto] items-center gap-x-3.5 border-b border-gray-100 px-5 py-2.5">
    <span class="text-xs tabular-nums text-gray-500">{{ time }}</span>
    <span class="h-7 w-[3px] rounded-sm" :style="{ background: color }" />
    <span class="inline-flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold text-white" :style="{ background: avatarColor }">{{ initial }}</span>
    <span class="min-w-0 truncate text-sm text-gray-700">
      <span class="font-semibold text-gray-900">{{ event.actor.name }}</span>
      <span class="font-semibold" :class="verbClass">{{ ' ' + description.verb + ' ' }}</span>
      <button v-if="subject.poLink" type="button" class="font-semibold text-blue-700 hover:underline" @click="emit('selectPo', subject.poLink)">{{ subject.text }}</button>
      <RouterLink v-else-if="subject.trackerLink" :to="subject.trackerLink" class="font-semibold text-blue-700 hover:underline">{{ subject.text }}</RouterLink>
      <span v-else class="italic text-gray-700">{{ subject.text }}</span>
      <span class="text-gray-300"> · </span>{{ event.vendor.alias ?? event.vendor.name }}<span v-if="description.where" class="text-gray-300"> · </span>
      <span v-if="description.where" class="text-gray-600">{{ description.where }}</span>
    </span>
    <span class="inline-flex min-w-[220px] items-center justify-end gap-2">
      <template v-if="payload.action === PO_ACTIVITY_ACTION.SCHEDULED || payload.action === PO_ACTIVITY_ACTION.REMOVED">
        <QuantityPill v-for="portion in payload.portions" :key="portion.capacityTypeId" :code="portion.capacityTypeCode" :quantity="portion.quantity" :struck="payload.action === PO_ACTIVITY_ACTION.REMOVED" />
      </template>
      <template v-else-if="payload.action === PO_ACTIVITY_ACTION.MOVED">
        <span class="whitespace-nowrap text-gray-500" :title="weekDatesLabel(payload.fromWeekStart)">{{ weekNumberLabel(payload.fromWeekStart) }}</span>
        <span class="text-gray-400">→</span>
        <span class="whitespace-nowrap font-semibold text-gray-900" :title="weekDatesLabel(payload.toWeekStart)">{{ weekNumberLabel(payload.toWeekStart) }}</span>
      </template>
      <template v-else-if="payload.action === PO_ACTIVITY_ACTION.QUANTITY_CHANGED">
        <QuantityPill :code="payload.capacityTypeCode" :quantity="payload.before" tone="old" />
        <span class="text-gray-400">→</span>
        <QuantityPill :code="payload.capacityTypeCode" :quantity="payload.after" tone="new" />
      </template>
      <span v-else-if="payload.action === PO_ACTIVITY_ACTION.MARKED_SCHEDULED && payload.note" class="max-w-[260px] truncate text-[13px] italic text-gray-600">{{ `“${payload.note}”` }}</span>
      <span v-else-if="payload.action === PO_ACTIVITY_ACTION.NOTE_CHANGED" class="max-w-[260px] truncate text-[13px] italic text-gray-600">{{ payload.note ? `“${payload.note}”` : 'note cleared' }}</span>
    </span>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import QuantityPill from '@/components/activity/QuantityPill.vue';
  import { useAccess } from '@/composables/useAccess';
  import { PERMISSIONS } from '@/types/access';
  import type { PoActivityEvent } from '@/types/poActivity';
  import { PO_ACTIVITY_ACTION } from '@/types/poActivity';
  import { describeEvent, subjectOf, weekDatesLabel, weekNumberLabel } from '@/utils/poActivity';

  const props = defineProps<{ event: PoActivityEvent; color: string }>();
  const emit = defineEmits<{ selectPo: [poNumber: string] }>();

  const VERB_CLASS: Record<PoActivityEvent['action'], string> = {
    scheduled: 'text-green-700',
    moved: 'text-indigo-700',
    quantity_changed: 'text-amber-700',
    removed: 'text-red-700',
    note_changed: 'text-gray-600',
    marked_scheduled: 'text-green-700',
    unmarked_scheduled: 'text-gray-600',
    shipment_planned: 'text-green-700',
    shipment_moved: 'text-indigo-700',
    shipment_lines_changed: 'text-amber-700',
    shipment_note_changed: 'text-gray-600',
    shipment_removed: 'text-red-700',
    shipped: 'text-green-700',
    hawb_added: 'text-gray-600',
    packing_list_added: 'text-gray-600',
    delivery_requested: 'text-violet-700',
    delivery_request_withdrawn: 'text-gray-600',
    delivery_request_answered: 'text-violet-700',
  };
  // Stable per actor so the same person keeps one colour down the whole feed.
  const AVATAR_COLORS = ['#2563eb', '#7c3aed', '#0f766e', '#b45309', '#be123c', '#4338ca'];

  const payload = computed(() => props.event.payload);
  const description = computed(() => describeEvent(props.event));
  const { hasPermission } = useAccess();
  const subject = computed(() => subjectOf(props.event, hasPermission(PERMISSIONS.READ_PO_TRACKER)));
  const verbClass = computed(() => VERB_CLASS[props.event.action]);
  const time = computed(() => new Date(props.event.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }));
  const initial = computed(() => (props.event.actor.name.trim()[0] ?? '?').toUpperCase());
  const avatarColor = computed(() => {
    const hash = [...props.event.actor.email].reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
  });
</script>
